// SZOLGÁLTATÁSAIM — egy kártya szolgáltatásonként (csomagonként): név, gyakoriság, használat-alapú jelzés,
// közös-számlázás jelölés és az IDŐSZAKOK idővonala („2026. júl. – 2026. szept.: 2 350 250 Ft/hó · …").
// A szolgáltatás-csoportosítás szerver-oldali (a hozzárendelés-sorokból), a felület csak megjeleníti.
import type { PortalServiceGroup } from '@atlas/contracts';
import { Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PortalCard, PortalCardTitle } from '@/components/portal/portal-card';
import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalSharedBadge } from '@/components/portal/portal-shared-badge';
import { ServiceStatusBadge } from '@/components/service-assignments/service-status-badge';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { formatMoney } from '@/components/shared/money-block';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { formatMonthSpan, frequencyKey } from '@/features/portal/billing-format';
import { usePortal } from '@/features/portal/portal-context';
import { api } from '@/lib/api';

function ServiceCard({ group }: { group: PortalServiceGroup }): JSX.Element {
  const { t, i18n } = useTranslation();
  const freq = frequencyKey(group.frequency);
  return (
    <PortalCard
      data-testid="portal-service-card"
      leading={
        <span className="bg-brand-soft text-brand flex size-8 shrink-0 items-center justify-center rounded-[9px] [&>svg]:size-4">
          <Square3Stack3DIcon />
        </span>
      }
      title={<PortalCardTitle>{group.serviceName}</PortalCardTitle>}
      badges={
        <>
          {freq !== null && (
            <DomainBadge
              kind="frequency"
              value={freq}
              label={t(`portal.billing.frequency.${freq}`)}
            />
          )}
          {group.usageBased && (
            <DomainBadge
              kind="portalBilling"
              value="usage"
              label={t('portal.billing.services.usageBased')}
            />
          )}
          <PortalSharedBadge shared={group.shared} />
        </>
      }
      meta={
        <>
          {group.accountName !== null && (
            <Text variant="secondary" as="p">
              {group.accountName}
            </Text>
          )}
          {group.restricted && (
            <Text variant="secondary" as="p">
              {t('portal.billing.hiddenShared.restrictedHint')}
            </Text>
          )}
        </>
      }
    >
      <ul aria-label={t('portal.billing.services.periodsAria', { name: group.serviceName })}>
        {group.periods.map((p, i) => (
          <li
            key={`${p.from ?? ''}-${p.to ?? ''}-${i}`}
            className="border-border flex flex-col gap-1 border-t py-2 first:border-t-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <span className="flex flex-wrap items-center gap-1.5">
              <Text variant="secondary" as="span" className="tabular-nums">
                {formatMonthSpan(p.from, p.to, i18n.language)}
              </Text>
              {p.status === 'pending' && (
                <ServiceStatusBadge status="pending" label={t('portal.billing.services.pending')} />
              )}
              {p.estimated && (
                <DomainBadge
                  kind="portalBilling"
                  value="estimate"
                  label={t('portal.billing.plan.estimate')}
                />
              )}
            </span>
            {group.restricted ? (
              <DomainBadge
                kind="portalBilling"
                value="shared"
                label={t('portal.billing.hiddenShared.restrictedAmount')}
              />
            ) : p.amount !== null && p.currency !== null ? (
              <span className="font-mono text-[15px] font-semibold tabular-nums tracking-[-0.02em]">
                {formatMoney(p.amount, p.currency, i18n.language)}
                {freq !== null && (
                  <span className="text-muted-foreground text-[11px] font-normal">
                    {freq === 'one_time'
                      ? ` ${t('portal.billing.per.one_time')}`
                      : t(`portal.billing.per.${freq}`)}
                  </span>
                )}
              </span>
            ) : (
              <DomainBadge
                kind="portalBilling"
                value="usage"
                label={t('portal.billing.services.usageAmount')}
              />
            )}
          </li>
        ))}
      </ul>
    </PortalCard>
  );
}

export function PortalBillingServicesView(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accountId } = usePortal();
  const q = useQuery({
    // Ugyanaz a kulcs és kérés, mint a kezdőlap kártyáján — a cache közös; a SCOPE minden kérésben ott van.
    queryKey: portalKeys.services(accountId ?? undefined),
    queryFn: () =>
      api.portal.services.query({
        query: {
          lang: i18n.language === 'en' ? 'en' : 'hu',
          ...(accountId !== null ? { accountId } : {}),
        },
      }),
  });

  if (q.isLoading) return <Skeleton className="h-48 w-full" />;
  if (q.data?.status === 403) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  if (q.data?.status !== 200) {
    return <PortalErrorState onRetry={() => void q.refetch()} />;
  }
  const { services } = q.data.body;
  if (services.length === 0) {
    return (
      <EmptyState
        title={t('portal.billing.services.empty.title')}
        description={t('portal.billing.services.empty.desc')}
      />
    );
  }
  return (
    <div className="space-y-3.5">
      <Badge variant="muted" icon={<Square3Stack3DIcon />}>
        {t('portal.billing.services.countBadge', { count: services.length })}
      </Badge>
      <div className="grid items-start gap-3.5 lg:grid-cols-2">
        {services.map((g, i) => (
          <ServiceCard key={`${g.serviceName}-${i}`} group={g} />
        ))}
      </div>
    </div>
  );
}
